import { supabase } from './supabaseClient';
import { ScanHistoryItem, ApiProvider } from '../types';
import { statsEmitter } from '../utils/statsEmitter';
import { resolveSharedImageUrl } from './thriftalyzeDataService';

export interface ScanStats {
  totalScans: number;
  savedItems: number;
  thisMonth: number;
}

export interface CreateScanData {
  title: string;
  description?: string;
  value?: string;
  aiExplanation?: string;
  imageUrl?: string;
  apiProvider: ApiProvider;
  visualMatches?: any[];
}

export const scanService = {
  // Create a new scan record
  async createScan(scanData: CreateScanData): Promise<ScanHistoryItem> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Prepare the scan record with common fields
    const commonFields = {
      user_id: user.id,
      title: scanData.title,
      description: scanData.description,
      value: scanData.value,
      ai_explanation: scanData.aiExplanation,
      image_url: scanData.imageUrl,
      api_provider: scanData.apiProvider,
      visual_matches: scanData.visualMatches || [],
    };

    // First insert into scans table
    const { data: scansData, error: scansError } = await supabase
      .from('scans')
      .insert({
        ...commonFields,
        is_saved: false, // Use is_saved for scans table
      })
      .select('*')
      .single();

    if (scansError) {
      console.error('Failed to insert into scans table:', scansError);
      throw scansError;
    }

    // Now insert into scan_history table with matching ID
    const { error: historyError } = await supabase
      .from('scan_history')
      .insert({
        ...commonFields,
        id: scansData.id, // Use the same ID from scans insert
        timestamp: new Date().getTime(), // Required for scan_history
        is_favorite: false, // Use is_favorite for scan_history
      });
      
    if (historyError) {
      console.error('Failed to insert into scan_history table:', historyError);
      // Try to clean up the scans record since history insert failed
      await supabase.from('scans').delete().eq('id', scansData.id);
      throw historyError;
    }

    // Emit stats refresh event
    console.log('📊 Emitting stats refresh after scan creation');
    statsEmitter.emit();

    return {
      id: scansData.id,
      title: scansData.title,
      description: scansData.description || '',
      value: scansData.value || '',
      aiExplanation: scansData.ai_explanation || '',
      imageUrl: scansData.image_url,
      apiProvider: scansData.api_provider as ApiProvider,
      timestamp: new Date(scansData.created_at).getTime(),
      visualMatches: scansData.visual_matches,
      isFavorite: scansData.is_saved,
    };
  },

  // Get user's scan history
  async getScanHistory(): Promise<ScanHistoryItem[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    try {
      const { data, error } = await supabase
        .from('scans')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        // Check if it's a table not found error
        if (error.code === '42P01' || error.message.includes('does not exist')) {
          console.warn('Scans table does not exist. Please run the database setup script.');
          return [];
        }
        throw error;
      }

      return Promise.all(data.map(async scan => {
        const result = scan.result && typeof scan.result === 'object' ? scan.result as Record<string, any> : {};
        const comparables = Array.isArray(result.comparables) ? result.comparables.map((item: Record<string, any>) => ({
          title: item.title || 'Marketplace listing',
          link: item.url || item.link || '',
          source: item.source || '',
          price: Number.isFinite(Number(item.price)) ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(item.price) + (Number(item.shippingPrice) || 0)) : undefined,
        })) : scan.visual_matches;
        return {
          id: scan.id,
          title: result.name || scan.title,
          description: result.description || scan.description || '',
          value: scan.value || (Number.isFinite(Number(result.suggestedListPrice)) ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(result.suggestedListPrice)) : 'No verified resale value'),
          aiExplanation: result.valuationBasis || scan.ai_explanation || '',
          imageUrl: await resolveSharedImageUrl(scan.image_url),
          apiProvider: scan.api_provider as ApiProvider,
          timestamp: new Date(scan.created_at).getTime(),
          visualMatches: comparables,
          isFavorite: scan.is_saved,
        };
      }));
    } catch (error) {
      console.error('Database error in getScanHistory:', error);
      throw error;
    }
  },

  // Get saved items
  async getSavedItems(): Promise<ScanHistoryItem[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    try {
      const { data, error } = await supabase
        .from('scans')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_saved', true)
        .order('created_at', { ascending: false });

      if (error) {
        // Check if it's a table not found error
        if (error.code === '42P01' || error.message.includes('does not exist')) {
          console.warn('Scans table does not exist. Please run the database setup script.');
          return [];
        }
        throw error;
      }

      return Promise.all(data.map(async scan => ({
        id: scan.id,
        title: scan.title,
        description: scan.description || '',
        value: scan.value || '',
        aiExplanation: scan.ai_explanation || '',
        imageUrl: await resolveSharedImageUrl(scan.image_url),
        apiProvider: scan.api_provider as ApiProvider,
        timestamp: new Date(scan.created_at).getTime(),
        visualMatches: scan.visual_matches,
        isFavorite: scan.is_saved,
      })));
    } catch (error) {
      console.error('Database error in getSavedItems:', error);
      throw error;
    }
  },

  // Toggle saved status
  async toggleSaved(scanId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // First get current saved status
    const { data: currentScan, error: fetchError } = await supabase
      .from('scans')
      .select('is_saved')
      .eq('id', scanId)
      .eq('user_id', user.id)
      .single();

    if (fetchError) throw fetchError;

    // Toggle the status in both tables
    const newStatus = !currentScan.is_saved;

    // Update in transaction-like manner
    const { error: scansError } = await supabase
      .from('scans')
      .update({ is_saved: newStatus })
      .eq('id', scanId)
      .eq('user_id', user.id);

    if (scansError) throw scansError;

    // Also update scan_history for consistency
    const { error: historyError } = await supabase
      .from('scan_history')
      .update({ is_favorite: newStatus })
      .eq('id', scanId)
      .eq('user_id', user.id);

    if (historyError) {
      // If history update fails, revert scans update to maintain consistency
      await supabase
        .from('scans')
        .update({ is_saved: currentScan.is_saved })
        .eq('id', scanId)
        .eq('user_id', user.id);
      throw historyError;
    }
  },

  // Delete a scan
  async deleteScan(scanId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Delete from both tables for consistency, scans table first
    // We do this sequentially to ensure foreign key constraints are respected
    await supabase
      .from('scans')
      .delete()
      .eq('id', scanId)
      .eq('user_id', user.id);
      
    await supabase
      .from('scan_history')
      .delete()
      .eq('id', scanId)
      .eq('user_id', user.id);

    // Emit stats refresh event
    console.log('📊 Emitting stats refresh after scan deletion');
    statsEmitter.emit();
  },

  // Get user stats
  async getUserStats(): Promise<ScanStats> {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    
    if (authError) {
      console.error('Auth error in getUserStats:', authError);
      throw new Error('Authentication failed');
    }
    
    if (!user) {
      console.log('No user found in getUserStats');
      return {
        totalScans: 0,
        savedItems: 0,
        thisMonth: 0,
      };
    }

    try {
      console.log('Getting stats for user:', user.id);

      // Get total scans count for this user
      const { count: totalScans, error: totalError } = await supabase
        .from('scans')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      if (totalError) {
        console.error('Error getting total scans:', totalError);
        throw totalError;
      }

      // Get saved items count for this user
      const { count: savedItems, error: savedError } = await supabase
        .from('scans')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('is_saved', true);

      if (savedError) {
        console.error('Error getting saved items:', savedError);
        throw savedError;
      }

      // Get this month's scans count for this user
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { count: thisMonth, error: monthError } = await supabase
        .from('scans')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .gte('created_at', startOfMonth.toISOString());

      if (monthError) {
        console.error('Error getting this month scans:', monthError);
        throw monthError;
      }

      console.log('Stats loaded successfully:', { totalScans, savedItems, thisMonth });

      return {
        totalScans: totalScans || 0,
        savedItems: savedItems || 0,
        thisMonth: thisMonth || 0,
      };
    } catch (error) {
      console.error('Database error in getUserStats:', error);
      throw error;
    }
  },
};
